import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, FileArrowDown } from "@phosphor-icons/react";
import { MermaidDiagram } from "@/components/MermaidDiagram";
import Header from "@/components/Header";
import { CALENDLY_URL } from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PATTERN_LINKS = {
  "esg-data-governance-medallion-audit-lineage": {
    to: "/patterns/medallion-spine",
    text: "Same spine, two budgets — see this architecture paired with the trade-promotion P&L",
  },
  "ai-trade-promotion-optimization": {
    to: "/patterns/medallion-spine",
    text: "Same spine, two budgets — see this architecture paired with the ESG legal budget",
  },
  "mainframe-data-liberation-mips-optimization": {
    to: "/patterns/cdc-deal-closer",
    text: "One stream, three deals — see this CDC spine paired with healthcare custody and replication risk",
  },
  "healthcare-clinical-data-engine": {
    to: "/patterns/cdc-deal-closer",
    text: "One stream, three deals — see this CDC spine paired with mainframe cost and replication risk",
  },
  "replication-risk-scoring-preflight": {
    to: "/patterns/cdc-deal-closer",
    text: "One stream, three deals — see this CDC spine paired with mainframe cost and healthcare custody",
  },
};
const EASE = [0.16, 1, 0.3, 1];

const formatDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

const BulletItem = ({ text }) => {
  const splitAt = text.indexOf(": ");
  const lead = splitAt > -1 ? text.slice(0, splitAt) : null;
  const rest = splitAt > -1 ? text.slice(splitAt + 2) : text;
  return (
    <li className="flex gap-4 text-base sm:text-lg font-light leading-relaxed text-white/70">
      <span className="mt-[0.7rem] h-px w-6 shrink-0 bg-[#E6E4D9]/50" />
      <span>
        {lead && (
          <span className="text-white font-normal">{lead}: </span>
        )}
        {rest}
      </span>
    </li>
  );
};

const Block = ({ block }) => {
  if (block.type === "heading") {
    return (
      <h2 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mt-14 mb-6">
        {block.text}
      </h2>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="border-l-2 border-[#E6E4D9] pl-6 lg:pl-8 my-12 font-serif italic text-xl sm:text-2xl text-[#E6E4D9] leading-relaxed">
        {block.text}
      </blockquote>
    );
  }
  if (block.type === "bullets") {
    return (
      <ul className="space-y-5 mb-8">
        {(block.items || []).map((item, j) => (
          <BulletItem key={j} text={item} />
        ))}
      </ul>
    );
  }
  if (block.type === "callouts") {
    const items = block.items || [];
    return (
      <div
        data-testid="article-outcome-callouts"
        className="my-12 grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10"
      >
        {items.map((item, j) => {
          const splitAt = item.indexOf(": ");
          const lead = splitAt > -1 ? item.slice(0, splitAt) : item;
          const rest = splitAt > -1 ? item.slice(splitAt + 2) : "";
          const isOrphan = items.length % 2 === 1 && j === items.length - 1;
          return (
            <div
              key={j}
              className={`bg-[#0A0A0A] p-6 lg:p-8 ${isOrphan ? "sm:col-span-2" : ""}`}
            >
              <span className="block font-serif italic text-xl sm:text-2xl text-[#E6E4D9] mb-3">
                {lead}
              </span>
              {rest && (
                <span className="block text-sm font-light leading-relaxed text-white/60">
                  {rest}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  if (block.type === "diagram") {
    return <MermaidDiagram code={block.text} />;
  }
  if (block.type === "flowcards") {
    const TINTS = {
      amber: { border: "border-amber-400/40", bg: "bg-amber-400/[0.07]", text: "text-amber-300" },
      blue: { border: "border-blue-400/40", bg: "bg-blue-400/[0.07]", text: "text-blue-300" },
      sky: { border: "border-sky-300/40", bg: "bg-sky-300/[0.07]", text: "text-sky-200" },
      grey: { border: "border-gray-400/40", bg: "bg-gray-400/[0.07]", text: "text-gray-300" },
      green: { border: "border-emerald-400/40", bg: "bg-emerald-400/[0.07]", text: "text-emerald-300" },
    };
    return (
      <div data-testid="article-flowcards" className="my-12 lg:my-16">
        <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight text-center">
          {block.title}
        </h3>
        <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/45">
          {block.subtitle}
        </p>
        <div className="mt-10 max-w-xl mx-auto">
          {block.stages.map((stage, i) => {
            const tint = TINTS[stage.color] || TINTS.grey;
            return (
              <div key={stage.name}>
                {i > 0 && (
                  <div className="flex justify-center py-2" aria-hidden="true">
                    <span className="block w-px h-6 bg-white/20" />
                  </div>
                )}
                <div
                  data-testid={`flowcard-${i + 1}`}
                  className={`border ${tint.border} ${tint.bg} px-6 lg:px-8 py-6 text-center`}
                >
                  <p className={`font-mono text-xs uppercase tracking-[0.25em] ${tint.text} mb-3`}>
                    {stage.name}
                  </p>
                  <p className="text-sm font-light leading-relaxed text-white/70">
                    {stage.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-8 max-w-xl mx-auto text-center text-sm font-light italic leading-relaxed text-white/50">
          {block.footer}
        </p>
      </div>
    );
  }
  if (block.type === "note") {
    const noteId = block.text
      .slice(0, 24)
      .toLowerCase()
      .replace(/[^a-z]+/g, "-");
    return (
      <div
        data-testid={`article-note-${noteId}`}
        className="my-10 border border-white/10 bg-white/[0.02] p-6 lg:p-8"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]/70 mb-3">
          Scope &amp; Methodology
        </p>
        <p className="text-sm font-light leading-relaxed text-white/60">
          {block.text}
        </p>
      </div>
    );
  }
  return (
    <p className="text-base sm:text-lg font-light leading-relaxed text-white/70 mb-7">
      {block.text}
    </p>
  );
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    window.scrollTo(0, 0);
    setStatus("loading");
    axios
      .get(`${API}/posts/${slug}`)
      .then((res) => {
        setPost(res.data);
        setStatus("ok");
      })
      .catch(() => setStatus("error"));
  }, [slug]);

  useEffect(() => {
    if (status === "ok" && post) {
      document.title = `${post.title} | TECC Insights`;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", post.excerpt);
    }
  }, [status, post]);

  useEffect(
    () => () => {
      document.title = "Tech Executive Capability Center LLC";
    },
    []
  );

  return (
    <div data-testid="article-page" className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 px-6 lg:px-12 pt-32 lg:pt-40 pb-24">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/#insights"
            data-testid="article-back-link"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white transition-colors duration-300 mb-12"
          >
            <ArrowLeft size={14} />
            All Insights
          </Link>

          {status === "loading" && (
            <div className="animate-pulse">
              <div className="h-3 w-40 bg-white/10 mb-8" />
              <div className="h-12 w-full bg-white/10 mb-4" />
              <div className="h-12 w-2/3 bg-white/10 mb-12" />
              <div className="h-4 w-full bg-white/5 mb-4" />
              <div className="h-4 w-full bg-white/5 mb-4" />
              <div className="h-4 w-3/4 bg-white/5" />
            </div>
          )}

          {status === "error" && (
            <div data-testid="article-not-found" className="py-16">
              <h1 className="font-serif text-4xl sm:text-5xl text-white tracking-tight mb-6">
                Insight not found
              </h1>
              <p className="text-white/60 font-light mb-10">
                This article may have been moved or unpublished.
              </p>
              <a
                href="/#insights"
                className="inline-flex items-center gap-2 border border-white/25 text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
              >
                Back to Insights
              </a>
            </div>
          )}

          {status === "ok" && post && (
            <motion.article
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE }}
            >
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9]">
                  {post.category}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
                  {formatDate(post.published_at)} · {post.read_time}
                </span>
                <Link
                  to="/who-am-i"
                  data-testid="article-byline"
                  className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-[#E6E4D9] transition-colors duration-300"
                >
                  By Amit Parikh — Founder &amp; Principal
                </Link>
              </div>

              <h1
                data-testid="article-title"
                className="font-serif tracking-[-0.02em] leading-[1.1] text-4xl sm:text-5xl lg:text-6xl text-white mb-10"
              >
                {post.title}
              </h1>

              <p className="font-serif italic text-xl sm:text-2xl text-white/60 leading-relaxed pb-10 border-b border-white/10">
                {post.excerpt}
              </p>

              {post.pdf_url && (
                <a
                  href={post.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="article-pdf-download"
                  className="group mt-10 inline-flex items-center gap-3 border border-white/25 text-white font-mono text-xs uppercase tracking-[0.15em] px-7 py-4 hover:bg-white hover:text-black transition-colors duration-300"
                >
                  <FileArrowDown size={16} weight="bold" />
                  Download the Full Whitepaper (PDF)
                </a>
              )}

              <div data-testid="article-content" className="mt-12">
                {post.content.map((block, i) => (
                  <div key={i}>
                    <Block block={block} />
                    {block.type === "callouts" && post.sources && (
                      <p
                        data-testid="article-sources-footnote"
                        className="-mt-6 mb-12 font-mono text-[10px] uppercase tracking-[0.2em] leading-relaxed text-white/35"
                      >
                        {post.sources}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {PATTERN_LINKS[slug] && (
                <Link
                  to={PATTERN_LINKS[slug].to}
                  data-testid="article-pattern-link"
                  className="group mt-12 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 border border-[#E6E4D9]/25 bg-[#E6E4D9]/[0.03] px-6 py-5 hover:bg-[#E6E4D9]/[0.06] transition-colors duration-500"
                >
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E6E4D9] shrink-0">
                    Pattern Study
                  </span>
                  <span className="text-sm font-light text-white/75">
                    {PATTERN_LINKS[slug].text}
                  </span>
                  <ArrowUpRight
                    size={14}
                    weight="bold"
                    className="sm:ml-auto shrink-0 text-white/30 transition-colors duration-300 group-hover:text-[#E6E4D9]"
                  />
                </Link>
              )}

              <div className="mt-16 border border-white/10 p-8 lg:p-12">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 mb-4">
                  Continue the conversation
                </p>
                <h3 className="font-serif text-2xl sm:text-3xl text-white tracking-tight mb-8">
                  If this pattern matches a pursuit,{" "}
                  <span className="italic text-[#E6E4D9]">let's sit the room.</span>
                </h3>
                <div className="flex flex-col lg:flex-row lg:items-end gap-8 lg:gap-12">
                  <LeadCapture source={post.title} />
                  <a
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="article-cta-button"
                    className="group shrink-0 inline-flex items-center gap-3 bg-[#E6E4D9] text-black font-mono text-xs uppercase tracking-[0.15em] px-7 py-4 hover:bg-[#C2C0B6] transition-colors duration-300"
                  >
                    Book a Strategy Call
                    <ArrowUpRight
                      size={14}
                      weight="bold"
                      className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </a>
                </div>
              </div>
            </motion.article>
          )}
        </div>
      </main>

      <footer className="border-t border-white/10 px-6 lg:px-12 py-8">
        <div className="max-w-3xl mx-auto flex items-center gap-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">
            © 2026 Tech Executive Capability Center LLC
          </p>
          <a
            href="/privacy"
            data-testid="footer-privacy-link"
            className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white transition-colors duration-300"
          >
            Privacy Policy
          </a>
        </div>
      </footer>
    </div>
  );
}

const LeadCapture = ({ source }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      await axios.post(`${API}/leads`, { email, source });
      setState("done");
      setEmail("");
    } catch (err) {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <p
        data-testid="blueprint-lead-success"
        className="flex-1 font-mono text-xs uppercase tracking-[0.2em] text-[#E6E4D9] lg:pb-4"
      >
        You are on the list — new blueprints land in your inbox.
      </p>
    );
  }

  return (
    <form
      data-testid="blueprint-lead-form"
      onSubmit={submit}
      className="flex-1 flex flex-col sm:flex-row gap-4 sm:items-end"
    >
      <div className="flex-1">
        <label
          htmlFor="blueprint-lead-email"
          className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 block mb-1"
        >
          Get new blueprints by email
        </label>
        <input
          id="blueprint-lead-email"
          data-testid="blueprint-lead-email-input"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#E6E4D9] focus:outline-none focus:ring-0 rounded-none px-0 py-3 text-white placeholder:text-white/30 transition-colors duration-300"
        />
        {state === "error" && (
          <p data-testid="blueprint-lead-error" className="mt-2 text-sm text-red-400">
            Something went wrong — please try again.
          </p>
        )}
        <p className="mt-2 font-mono text-[10px] tracking-[0.12em] text-white/30">
          Only used to send new blueprints. No sharing, ever.
        </p>
      </div>
      <button
        type="submit"
        data-testid="blueprint-lead-submit"
        disabled={state === "sending"}
        className="shrink-0 inline-flex items-center gap-2 border border-white/25 text-white font-mono text-[10px] uppercase tracking-[0.2em] px-6 py-3.5 hover:bg-white hover:text-black transition-colors duration-300 disabled:opacity-50"
      >
        {state === "sending" ? "Joining..." : "Notify Me"}
      </button>
    </form>
  );
};
